// src/pages/DeviceAdminPage.tsx
import React, { useEffect, useState } from "react";
import SideMenu from "../../../components/sideMenu";
import DropdownInput from "../../../components/dropdownInput";
import AlertBox from "../../../components/alertBox";
import Loading from "../../../components/loading";
import { organizationService } from "../../../services/organizationService";
import { deviceService } from "../../../services/deviceService";
import {
  AlertConfig,
  Device,
  DeviceStatus,
  Organization,
  User,
} from "../../../type";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import DropZoneUpload from "../../../components/uploadDropZone";
import { userService } from "../../../services/userService";
import { roleService } from "../../../services/roleService";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiRepeat,
  FiRotateCcw,
  FiXCircle,
} from "react-icons/fi";

const PAGE_SIZE = 9; // แสดง 9 การ์ดต่อหน้า
const PAGE_RANGE = 2;
const ORG_LIMIT = 10;

const DeviceAdminPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [selectedOrgIdx, setSelectedOrgIdx] = useState<number | undefined>(0);
  const [targetOrgIdx, setTargetOrgIdx] = useState<number | undefined>();
  const [devices, setDevices] = useState<Device[]>([]);
  const [deletedDevices, setDeletedDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [readOnly, setReadOnly] = useState(true);

  const [deviceId, setDeviceId] = useState<number>();
  const [deviceName, setDeviceName] = useState("");
  const [deviceDescription, setDeviceDescription] = useState("");
  const [deviceImageUrl, setDeviceImageUrl] = useState("");
  const [deviceDiagramUrl, setDeviceDiagramUrl] = useState("");
  const [isPlatformSuperAdmin, setIsPlatformSuperAdmin] = useState(false);

  // States สำหรับ popup ตั้งเวลา
  const [openAccessTimeForm, setOpenAccessTimeForm] = useState(false);
  const [accessTimeDevice, setAccessTimeDevice] = useState<Device | null>(null);
  const [accessStartTimeStr, setAccessStartTimeStr] = useState("");
  const [accessEndTimeStr, setAccessEndTimeStr] = useState("");

  const [effective, setEffective] = useState<Record<string, boolean>>({});
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  const [activeTab, setActiveTab] = useState<"active" | "deleted">("active");
  const [orgPage, setOrgPage] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [errors, setErrors] = useState({
    deviceName: "",
    deviceImageUrl: "",
    deviceDiagramUrl: "",
  });

  const navigate = useNavigate();

  const fetchOrgs = async (page = 1, append = false) => {
    setIsLoading(true);
    try {
      const res = await organizationService.getOrganizations({ page, limit: ORG_LIMIT });
      if (append) {
        setOrganizations(prev => [...prev, ...res.data]);
      } else {
        setOrganizations(res.data);
        if (res.data.length > 0) {
          setSelectedOrgIdx(0);
          await fetchDevices(res.data[0], 1);
          await fetchDeletedDevices(res.data[0], 1);
        }
      }
      setTotalOrgs(res.total ?? 0);
    } finally {
      setIsLoading(false);
    }
  };

  // เพิ่มฟังก์ชันสำหรับโหลดข้อมูลต่อ
  const handleLoadMoreOrgs = () => {
    if (organizations.length < totalOrgs) {
      const nextPage = orgPage + 1;
      setOrgPage(nextPage);
      fetchOrgs(nextPage, true);
    }
  };

  const fetchAllOrgs = async () => {
    setIsLoading(true);
    try {
      const res = await organizationService.getAllOrganizations();
      setAllOrgs(res.data);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDevices = async (org: Organization, page: number = currentPage) => {
    setIsLoading(true);
    try {
      const res = await deviceService.getDevices({ org_name: org.orgName, page, limit: PAGE_SIZE });
      setDevices(res.data);
      if (activeTab === "active") setTotalPages(Math.ceil((res.total || 0) / PAGE_SIZE));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeletedDevices = async (org: Organization, page: number = currentPage) => {
    setIsLoading(true);
    try {
      const res = await deviceService.getDeletedDevices({
        org_name: org.orgName, page, limit: PAGE_SIZE
      });
      setDeletedDevices(res.data);
      if (activeTab === "deleted") setTotalPages(Math.ceil((res.total || 0) / PAGE_SIZE));
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    let valid = true;

    const newErrors = {
      deviceName: "",
      deviceImageUrl: "",
      deviceDiagramUrl: "",
    };

    if (!deviceName.trim()) {
      newErrors.deviceName = "Device name is required.";
      valid = false;
    }

    if (!deviceImageUrl) {
      newErrors.deviceImageUrl = "Device image is required.";
      valid = false;
    }

    if (!deviceDiagramUrl) {
      newErrors.deviceDiagramUrl = "Device diagram is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [profileRes, effectiveRes] = await Promise.all([
          userService.getProfile(),
          roleService.getMyEffectivePermissions(),
        ]);

        setEffective(effectiveRes.effective || {});

        if (profileRes.isPlatformSuperAdmin) {
          setIsPlatformSuperAdmin(true);
        }
        await fetchOrgs();
      } catch (err) {
        console.error("[DeviceAdminPage] init error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  // UseEffect for Pagination / Tab changing
  useEffect(() => {
    if (selectedOrgIdx !== undefined && organizations[selectedOrgIdx]) {
      const org = organizations[selectedOrgIdx];
      if (activeTab === "active") {
        fetchDevices(org, currentPage);
      } else {
        fetchDeletedDevices(org, currentPage);
      }
    }
  }, [currentPage, activeTab]);

  const resetForm = () => {
    setEditingDevice(null);
    setDeviceName("");
    setDeviceImageUrl("");
    setDeviceDiagramUrl("");
    setErrors({
      deviceName: "",
      deviceImageUrl: "",
      deviceDiagramUrl: "",
    });
  };

  const onSelectOrg = async (idx: number) => {
    setSelectedOrgIdx(idx);
    setCurrentPage(1); // รีเซ็ตหน้าเมื่อเปลี่ยน Org
    const org = organizations[idx];
    if (org) {
      if (activeTab === "active") await fetchDevices(org, 1);
      else await fetchDeletedDevices(org, 1);
    }
  };

  const onChangeTab = (tab: "active" | "deleted") => {
    setActiveTab(tab);
    setCurrentPage(1); // รีเซ็ตหน้าเมื่อเปลี่ยน Tab
  }

  // Pagination Helper
  const getPaginationItems = (current: number, total: number) => {
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > PAGE_RANGE + 2) pages.push("...");
      for (let i = Math.max(2, current - PAGE_RANGE); i <= Math.min(total - 1, current + PAGE_RANGE); i++) {
        pages.push(i);
      }
      if (current < total - PAGE_RANGE - 1) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  // Helper สำหรับแปลงเวลาเพื่อยัดใส่ <input type="datetime-local" />
  const formatForDateTimeLocal = (date?: Date | string | null) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openCreateForm = () => {
    setEditingDevice(null);
    setDeviceId(undefined);
    setDeviceName("");
    setDeviceDescription("");
    setDeviceImageUrl("");
    setDeviceDiagramUrl("");
    setErrors({
      deviceName: "",
      deviceImageUrl: "",
      deviceDiagramUrl: "",
    });
    setOpenForm(true);
  };

  const openEditForm = (d: Device) => {
    setEditingDevice(d);
    setDeviceName(d.deviceName);
    setDeviceDescription(d.deviceDescription ?? "");
    setDeviceImageUrl(d.deviceImageUrl);
    setDeviceDiagramUrl(d.deviceDiagramUrl ?? "");
    setDeviceId(d.deviceId);
    setErrors({
      deviceName: "",
      deviceImageUrl: "",
      deviceDiagramUrl: "",
    });
    setOpenForm(true);
  };

  const openAccessTimeModal = (d: Device) => {
    setAccessTimeDevice(d);
    setAccessStartTimeStr(formatForDateTimeLocal(d.dataAccessStartTime));
    setAccessEndTimeStr(formatForDateTimeLocal(d.dataAccessEndTime));
    setOpenAccessTimeForm(true);
  };

  const showSuccessAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "success",
      title,
      message,
    });
  };

  const showErrorAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "error",
      title,
      message,
    });
  };

  const showConfirmDeleteAlert = (orgName: string, onConfirm: () => void) => {
    setAlertConfig({
      type: "confirm",
      title: "Confirm deletion",
      message: `Are you sure you want to delete "${orgName}"?`,
      onConfirm,
    });
  };

  const showConfirmRestoreAlert = (orgName: string, onConfirm: () => void) => {
    setAlertConfig({
      type: "restore",
      title: "Confirm restoration",
      message: `Are you sure you want to restore "${orgName}"?`,
      onConfirm,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (selectedOrgIdx === undefined) return;
    const org = organizations[selectedOrgIdx];
    if (!org) return;

    setIsLoading(true);
    try {
      if (editingDevice) {
        await deviceService.updateDevice(editingDevice.deviceId, {
          device_name: deviceName,
          device_image: deviceImageUrl,
          device_description: deviceDescription,
          device_diagram: deviceDiagramUrl || null,
          org_id: org.id,
        });
        await fetchDevices(org, currentPage);
        setOpenForm(false);
        showSuccessAlert(
          "Updated successfully",
          `Device "${editingDevice.deviceName}" has been updated.`,
        );
        resetForm();
      } else {
        await deviceService.createDevice({
          device_name: deviceName,
          device_description: deviceDescription,
          device_image: deviceImageUrl,
          device_diagram: deviceDiagramUrl || null,
          org_id: org.id,
        });
        setCurrentPage(1);
        await fetchDevices(org, 1);
        setOpenForm(false);
        showSuccessAlert(
          "Created successfully",
          `Device "${deviceName}" has been created.`,
        );
        resetForm();
      }
    } catch (err) {
      console.error("Create/Update device failed", err);
      showErrorAlert(
        "Failed to save device",
        "An error occurred while creating or updating the device. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccessTimeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessTimeDevice) return;

    setIsLoading(true);
    try {
      const payload = {
        start_time: accessStartTimeStr ? new Date(accessStartTimeStr).toISOString() : undefined,
        end_time: accessEndTimeStr ? new Date(accessEndTimeStr).toISOString() : null,
      };

      await deviceService.updateDataAccessTime(accessTimeDevice.deviceId, payload);
      setOpenAccessTimeForm(false);
      showSuccessAlert("Updated successfully", "Device access time has been updated.");

      if (selectedOrgIdx !== undefined && organizations[selectedOrgIdx]) {
        await fetchDevices(organizations[selectedOrgIdx], currentPage);
      }
    } catch (err) {
      console.error("Update access time failed", err);
      showErrorAlert("Update Failed", "Could not update access time.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDeviceConfirmed = async (device: Device) => {
    setIsLoading(true);
    try {
      await deviceService.deleteDevice(device.deviceId);
      if (selectedOrgIdx === undefined) return;
      await fetchDevices(organizations[selectedOrgIdx], currentPage);
      await fetchDeletedDevices(organizations[selectedOrgIdx], 1);
      showSuccessAlert(
        "Deleted successfully",
        `Device "${device.deviceName}" has been deleted.`,
      );
    } catch (err) {
      console.error("Delete device failed", err);
      showErrorAlert(
        "Failed to delete device",
        "An error occurred while deleting the device. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreDeviceConfirmed = async (device: Device) => {
    setIsLoading(true);
    try {
      await deviceService.restoreDevice(device.deviceId);
      if (selectedOrgIdx === undefined) return;
      await fetchDevices(organizations[selectedOrgIdx], 1);
      await fetchDeletedDevices(organizations[selectedOrgIdx], currentPage);
      showSuccessAlert(
        "Restore successfully",
        `Device "${device.deviceName}" has been restored.`,
      );
    } catch (err) {
      console.error("Restore device failed", err);
      showErrorAlert(
        "Failed to restore device",
        "An error occurred while restoring the device. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferDevice = async (
    deviceId: number,
    organizationId: string,
  ) => {
    try {
      setIsLoading(true)
      await deviceService.transferDevice(deviceId, organizationId);

      setAlertConfig({
        type: "success",
        title: "Transfer Successful",
        message: "The device has been transferred successfully.",
      });

      setTargetOrgIdx(undefined);
      setAllOrgs([]);
      setIsLoading(false)

      if (selectedOrgIdx === undefined) return;
      await fetchDevices(organizations[selectedOrgIdx], currentPage);
      await fetchDeletedDevices(organizations[selectedOrgIdx], currentPage);
    } catch (error: any) {
      setAlertConfig({
        type: "error",
        title: "Transfer Failed",
        message: error?.message || "Something went wrong.",
      });
    }
  };

  const handleDelete = (device: Device) => {
    showConfirmDeleteAlert(device.deviceName, () =>
      handleDeleteDeviceConfirmed(device),
    );
  };

  const handleRestore = (device: Device) => {
    showConfirmRestoreAlert(device.deviceName, () =>
      handleRestoreDeviceConfirmed(device),
    );
  };

  const canCreateDevice = effective["device.create"] || false;
  const canUpdateDevice = effective["device.update"] || false;
  const canDeleteDevice = effective["device.delete"] || false;
  const canRestoreDevice = effective["device.delete"] || false;
  const displayedDevices = activeTab === "active" ? devices : deletedDevices;

  return (
    <SideMenu active={3}>
      <div className="relative">
        {isLoading && <Loading />}
        <div className="flex">
          <div className="container p-4 pt-8 xl:pt-4">
            <div className="flex md:flex-row flex-col gap-4 justify-between mb-4">
              <div className="flex gap-4 items-center">
                <FaCircleArrowLeft
                  className="text-2xl cursor-pointer"
                  onClick={() => navigate(-1)}
                />
                <h3 className="text-xl font-bold">Device Management</h3>
              </div>
              {canCreateDevice && (
                <button
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm"
                  onClick={openCreateForm}
                >
                  + New Device
                </button>
              )}
            </div>

            {isPlatformSuperAdmin && (
              <div className="flex md:flex-row flex-col items-start md:items-center justify-between mb-6">
                <p className="text-sm text-gray-600">
                  Select organization to manage its devices.
                </p>
                {organizations.length > 0 && selectedOrgIdx !== undefined && (
                  <div className="md:w-64 w-full">
                    <DropdownInput
                      options={organizations.map((o) => o.orgName)}
                      value_idx={selectedOrgIdx}
                      onSelect={(e) => {
                        onSelectOrg(e);
                      }}
                      onReachBottom={handleLoadMoreOrgs}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                className={`px-4 py-2 text-sm rounded ${
                  activeTab === "active"
                    ? "bg-green text-white"
                    : "bg-gray-nor text-white"
                }`}
                onClick={() => onChangeTab("active")}
              >
                Active Devices
              </button>

              <button
                className={`px-4 py-2 text-sm rounded ${
                  activeTab === "deleted"
                    ? "bg-green text-white"
                    : "bg-gray-nor text-white"
                }`}
                onClick={() => onChangeTab("deleted")}
              >
                Deleted Devices
              </button>
            </div>

            <div className="mt-4">
              {displayedDevices.length > 0 ? (
                <>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {activeTab === "active"
                      ? "Devices List"
                      : "Deleted Devices"}
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-2 mb-6">
                    {displayedDevices.map((d) => {
                      const isActive = d.status === DeviceStatus.ACTIVE;

                      return (
                        <div
                          key={d.deviceId}
                          className="p-3 shadow-lg rounded-lg cursor-pointer hover:shadow-xl transition flex flex-col justify-between"
                          onClick={() => {
                            if (activeTab === "active") {
                              openEditForm(d);
                              setReadOnly(!canUpdateDevice);
                            }
                          }}
                        >
                          <div>
                            <img
                              src={d.deviceImageUrl}
                              alt={d.deviceName}
                              className="w-full h-36 object-contain bg-gray-50 rounded-md"
                            />

                            <div className="flex justify-between mt-4 mb-2 items-start">
                              <div>
                                <p className="font-bold text-sm mb-1">
                                  {d.deviceName}
                                </p>

                                {activeTab === "active" ? (
                                  <p className="text-[11px] text-gray-400">
                                    Created:{" "}
                                    {new Date(d.createdAt).toLocaleDateString()}
                                  </p>
                                ) : (
                                  <p className="text-[11px] text-gray-400">
                                    Deleted:{" "}
                                    {new Date(d.deletedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>

                              {activeTab === "active" && d.status && (
                                <div className="flex gap-2 items-center">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      isActive ? "bg-green" : "bg-red"
                                    }`}
                                  />
                                  <p
                                    className={`text-xs ${
                                      isActive ? "text-green" : "text-red"
                                    }`}
                                  >
                                    {d.status}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap justify-end gap-2 mt-2">
                            {activeTab === "active" && (
                              <>
                                {canUpdateDevice && (
                                  <button
                                    className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setReadOnly(!canUpdateDevice);
                                      openEditForm(d);
                                    }}
                                  >
                                    Edit
                                  </button>
                                )}

                                {canDeleteDevice && (
                                  <button
                                    className="px-2 py-1 text-xs rounded bg-red text-white"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(d);
                                    }}
                                  >
                                    Delete
                                  </button>
                                )}

                                {isPlatformSuperAdmin && (
                                  <>
                                    <button
                                      type="button"
                                      className="px-2 py-1 text-xs rounded bg-navy text-white hover:opacity-90"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        await fetchAllOrgs();

                                        setAlertConfig({
                                          type: "transfer",
                                          title: "Transfer Device",
                                          message:
                                            "Please select an organization to transfer this device to.",
                                          onConfirm: async (
                                            organizationId: string,
                                          ) => {
                                            await handleTransferDevice(
                                              d.deviceId,
                                              organizationId,
                                            );
                                          },
                                        });
                                      }}
                                    >
                                      Transfer
                                    </button>
                                    <button
                                      type="button"
                                      className="px-2 py-1 text-xs rounded border border-navy text-navy hover:bg-navy hover:text-white transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAccessTimeModal(d);
                                      }}
                                    >
                                      Time Limit
                                    </button>
                                  </>
                                )}
                              </>
                            )}

                            {activeTab === "deleted" && canRestoreDevice && (
                              <button
                                className="px-2 py-1 text-xs rounded bg-green text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore(d);
                                }}
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Cards */}
                  {totalPages > 1 && (
                    <div className="flex flex-row items-center justify-center gap-3 w-full mt-6">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                        <img src="../right-arrow.svg" alt="Prev" className="w-5 rotate-180 opacity-80" />
                      </button>
                      <div className="flex space-x-2">
                        {getPaginationItems(currentPage, totalPages).map((page, i) => (
                          <button
                            key={i}
                            className={`px-3 py-1 rounded text-sm ${currentPage === page ? "bg-green text-white" : "bg-gray-200 text-black"} ${page === "..." ? "cursor-default" : ""}`}
                            onClick={() => typeof page === "number" && setCurrentPage(page)}
                            disabled={page === "..."}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                        <img src="../right-arrow.svg" alt="Next" className="w-5 opacity-80" />
                      </button>
                    </div>
                  )}

                </>
              ) : (
                <div className="mt-4 mb-6 text-center text-gray-500 text-sm">
                  No devices found.
                </div>
              )}
            </div>

          </div>

          {/* Device Modal (Create/Edit) */}
          <AlertBox open={openForm}>
            <div className="w-[320px] md:w-[480px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingDevice ? "Edit Device" : "Create Device"}
                </h3>
                <button onClick={() => setOpenForm(false)}>✕</button>
              </div>
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="flex gap-3">
                  {deviceId && (
                    <div className="w-1/5">
                      <label className="block text-sm mb-1">Device Id</label>
                      <input
                        className={`w-full border rounded px-3 py-2 text-sm ring-0 outline-none`}
                        value={deviceId}
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                  <div className={`${deviceId ? "w-4/5" : "w-full"}`}>
                    <label className="block text-sm mb-1">Device Name</label>
                    <input
                      className={`w-full border rounded px-3 py-2 text-sm ${
                        errors.deviceName ? "border-red" : "border-gray-300"
                      }`}
                      value={deviceName}
                      readOnly={readOnly}
                      onChange={(e) => {
                        setDeviceName(e.target.value);
                        if (errors.deviceName) {
                          setErrors((prev) => ({ ...prev, deviceName: "" }));
                        }
                      }}
                    />
                    {errors.deviceName && (
                      <p className="text-xs text-red mt-1">
                        {errors.deviceName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <label className="block text-sm mb-1">
                    Device Description
                  </label>
                  <textarea
                    rows={5}
                    className={`w-full border rounded px-3 py-2 text-sm border-gray-300`}
                    value={deviceDescription}
                    readOnly={readOnly}
                    onChange={(e) => {
                      setDeviceDescription(e.target.value);
                    }}
                  />
                </div>

                <div>
                  <DropZoneUpload
                    label="Device Image"
                    value={deviceImageUrl}
                    onChange={(url: string) => {
                      setDeviceImageUrl(url);
                      if (errors.deviceImageUrl) {
                        setErrors((prev) => ({ ...prev, deviceImageUrl: "" }));
                      }
                    }}
                    height={150}
                    circle={false}
                    readOnly={readOnly}
                  />
                  {errors.deviceImageUrl && (
                    <p className="text-xs text-red mt-1">
                      {errors.deviceImageUrl}
                    </p>
                  )}
                </div>

                <div>
                  <DropZoneUpload
                    label="Device Diagram"
                    value={deviceDiagramUrl}
                    onChange={(url: string) => {
                      setDeviceDiagramUrl(url);
                      if (errors.deviceDiagramUrl) {
                        setErrors((prev) => ({
                          ...prev,
                          deviceDiagramUrl: "",
                        }));
                      }
                    }}
                    height={150}
                    circle={false}
                    readOnly={readOnly}
                  />
                  {errors.deviceDiagramUrl && (
                    <p className="text-xs text-red mt-1">
                      {errors.deviceDiagramUrl}
                    </p>
                  )}
                </div>

                {canUpdateDevice && (
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm rounded border"
                      onClick={() => setOpenForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        !!errors.deviceName ||
                        !!errors.deviceImageUrl ||
                        !!errors.deviceDiagramUrl
                      }
                      className={`px-4 py-2 text-sm rounded text-white
                    ${
                      !!errors.deviceName ||
                      !!errors.deviceImageUrl ||
                      !!errors.deviceDiagramUrl
                        ? "bg-gray-light cursor-not-allowed"
                        : "bg-navy"
                    }
                  `}
                    >
                      {editingDevice ? "Update" : "Create"}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </AlertBox>

          {/* Access Time Modal */}
          <AlertBox open={openAccessTimeForm}>
            <div className="w-[320px] md:w-[400px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Set Data Access Time Limit</h3>
                <button onClick={() => setOpenAccessTimeForm(false)}>✕</button>
              </div>
              <form className="flex flex-col gap-4" onSubmit={handleAccessTimeSubmit}>
                <div>
                  <label className="block text-sm mb-1">
                    Start Time <span className="text-red">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-navy"
                    value={accessStartTimeStr}
                    onChange={(e) => setAccessStartTimeStr(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">
                    End Time <span className="text-gray-400 text-xs">(Leave blank for no limit)</span>
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-navy"
                    value={accessEndTimeStr}
                    onChange={(e) => setAccessEndTimeStr(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded border"
                    onClick={() => setOpenAccessTimeForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!accessStartTimeStr}
                    className="px-4 py-2 text-sm rounded bg-navy text-white disabled:opacity-50"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </AlertBox>

          <AlertBox open={!!alertConfig}>
            {alertConfig && (
              <div className="w-[320px] md:w-[360px]">
                {alertConfig.type === "transfer" ? (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <div>
                        <FiRepeat className="text-green text-2xl" />
                      </div>

                      <h3 className="text-lg font-semibold">
                        {alertConfig.title}
                      </h3>
                    </div>

                    <p className="text-sm text-gray-700 mb-3">
                      {alertConfig.message}
                    </p>

                    <div className="mb-6">
                      {allOrgs.length > 0 && (
                        <div className="w-full">
                          <DropdownInput
                            options={allOrgs.map((o) => o.orgName)}
                            value_idx={targetOrgIdx ?? 0}
                            onSelect={(e) => {
                              console.log("target:", e);
                              if (e === 0) {
                                setTargetOrgIdx(0);
                              } else {
                                setTargetOrgIdx(e);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        className="px-4 py-2 text-sm rounded border"
                        onClick={() => setAlertConfig(null)}
                      >
                        Cancel
                      </button>

                      <button
                        className="px-4 py-2 text-sm rounded bg-navy text-white disabled:opacity-50"
                        disabled={targetOrgIdx === undefined}
                        onClick={() => {
                          if (targetOrgIdx === undefined) return;

                          const orgId = allOrgs[targetOrgIdx].id;

                          alertConfig.onConfirm?.(orgId);
                          setAlertConfig(null);
                        }}
                      >
                        Transfer
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* icon + title */}
                    <div className="flex items-center gap-3 mb-3">
                      <div>
                        {alertConfig.type === "success" && (
                          <FiCheckCircle className="text-green text-2xl" />
                        )}
                        {alertConfig.type === "error" && (
                          <FiXCircle className="text-red text-2xl" />
                        )}
                        {alertConfig.type === "confirm" && (
                          <FiAlertTriangle className="text-yellow text-2xl" />
                        )}
                        {alertConfig.type === "restore" && (
                          <FiRotateCcw className="text-green text-2xl" />
                        )}
                      </div>

                      <h3 className="text-lg font-semibold">
                        {alertConfig.title}
                      </h3>
                    </div>

                    {/* message */}
                    <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">
                      {alertConfig.message}
                    </p>

                    {/* buttons */}
                    <div className="flex justify-end gap-2">
                      {alertConfig.type === "confirm" ||
                      alertConfig.type === "restore" ? (
                        <>
                          <button
                            className="px-4 py-2 text-sm rounded border"
                            onClick={() => setAlertConfig(null)}
                          >
                            Cancel
                          </button>

                          <button
                            className={`px-4 py-2 text-sm rounded text-white
                    ${alertConfig.type === "confirm" ? "bg-red" : "bg-green"}`}
                            onClick={() => {
                              alertConfig.onConfirm?.();
                              setAlertConfig(null);
                            }}
                          >
                            {alertConfig.type === "confirm"
                              ? "Delete"
                              : "Restore"}
                          </button>
                        </>
                      ) : (
                        <button
                          className="px-4 py-2 text-sm rounded bg-navy text-white"
                          onClick={() => setAlertConfig(null)}
                        >
                          OK
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </AlertBox>
        </div>
      </div>
    </SideMenu>
  );
};

export default DeviceAdminPage;